import { Alert, Button, Checkbox, Dialog, Loading } from "@vellum/ui";
import type {
  DashboardSubscriptionSchema,
  SubscriptionAutoRecipientSchema,
} from "@vellum/vellum-api";
import { useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";

interface SendConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful send so the send history can refresh. */
  onSent?: () => void;
  subscription: DashboardSubscriptionSchema;
}

interface Recipient {
  email: string;
  /** Shown under the email for auto recipients (e.g. "Jane Doe • Account Manager"). */
  detail: string | null;
}

export function SendConfirmDialog({
  isOpen,
  onClose,
  onSent,
  subscription,
}: SendConfirmDialogProps) {
  const [autoRecipients, setAutoRecipients] = useState<
    SubscriptionAutoRecipientSchema[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [autoRecipientsError, setAutoRecipientsError] = useState<string | null>(
    null,
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setAutoRecipientsError(null);
    setSelected([]);
    setError(null);
    vellumApiSdk
      .listSubscriptionAutoRecipients({
        query: {
          type: subscription.typ,
          company_id: subscription.company_id,
          facility_id: subscription.facility_id ?? undefined,
        },
      })
      .then((res) => {
        setAutoRecipients(res.data ?? []);
      })
      .catch(() => {
        setAutoRecipientsError(
          "Something went wrong loading automatic recipients.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    isOpen,
    subscription.typ,
    subscription.company_id,
    subscription.facility_id,
  ]);

  // The subscription's own recipients first, then auto recipients not already
  // listed, deduped by email so an address is only shown (and sent) once.
  const recipients: Recipient[] = [];
  const seen = new Set<string>();
  for (const email of subscription.to_emails) {
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    recipients.push({ email, detail: null });
  }
  for (const recipient of autoRecipients) {
    const key = recipient.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    recipients.push({
      email: recipient.email,
      detail: `${recipient.name} • ${recipient.role}`,
    });
  }

  const allSelected =
    recipients.length > 0 && selected.length === recipients.length;

  const toggle = (email: string) => {
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  const toggleAll = () => {
    setSelected(
      allSelected ? [] : recipients.map((recipient) => recipient.email),
    );
  };

  const handleSend = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setError(null);
    try {
      const res = await vellumApiSdk.sendSubscription({
        path: { subscription_id: subscription.id },
        body: { to_emails: selected },
      });
      if (res.error) {
        setError(
          res.error.detail?.map((e) => e.msg).join(", ") ??
            "Failed to send report",
        );
      } else {
        onSent?.();
        onClose();
      }
    } catch {
      setError("Failed to send report");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Send ${subscription.typ} Report Now`}
      icon="iconoir-send"
      iconColor="blue"
    >
      <p className="mt-1 text-sm text-gray-500">
        Choose who should receive the {subscription.typ.toLowerCase()} report
        immediately.
      </p>

      {loading ? (
        <div className="mt-4 flex justify-center">
          <Loading />
        </div>
      ) : (
        <>
          {autoRecipientsError && (
            <div className="mt-3">
              <Alert>{autoRecipientsError}</Alert>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Recipients
            </span>
            {recipients.length > 0 && (
              <Button variant="outline" onClick={toggleAll}>
                {allSelected ? "Clear all" : "Select all"}
              </Button>
            )}
          </div>
          {recipients.length > 0 ? (
            <div className="mt-1 max-h-60 space-y-0.5 overflow-y-auto rounded-md bg-gray-50 p-2">
              {recipients.map((recipient) => (
                <div
                  key={recipient.email}
                  className="rounded-md px-2 py-2 hover:bg-gray-100"
                >
                  <Checkbox
                    containerClassName="cursor-pointer"
                    className="cursor-pointer"
                    checked={selected.includes(recipient.email)}
                    onChange={() => toggle(recipient.email)}
                    label={
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-gray-800">
                          {recipient.email}
                        </span>
                        {recipient.detail && (
                          <span className="block truncate text-xs text-gray-500">
                            {recipient.detail}
                          </span>
                        )}
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1 rounded-md bg-gray-50 p-3 text-sm text-gray-500">
              No recipients found for this subscription.
            </p>
          )}
        </>
      )}

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-3">
        <Button
          onClick={handleSend}
          disabled={sending || selected.length === 0}
          loading={sending}
        >
          Send Now
        </Button>
      </div>
    </Dialog>
  );
}
